import { useProfilePreview } from '../../lib/profilePreview';
import { LegacyWidgetDashboard } from './LegacyWidgetDashboard';
import { resolveProfile, userLanding, isPlatformAdmin } from '../../lib/profileAccess';
import { NoAccessPage } from '../../components/auth/ProfileRouteGuard';
import { HrDashboard } from './roles/HrDashboard';
import { CrmOperationsDashboard } from './roles/CrmOperationsDashboard';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { resolveRoleKey, type DashboardRoleKey } from './roleLayouts';
import { AdminDashboard } from './roles/AdminDashboard';
import { SalesDashboard } from './roles/SalesDashboard';
import { AccountantDashboard } from './roles/AccountantDashboard';
import { InventoryDashboard } from './roles/InventoryDashboard';
import { ProductionDashboard } from './roles/ProductionDashboard';
import ProcurementDashboard from './roles/ProcurementDashboard';
import ProjectDashboard from './roles/ProjectDashboard';
// Apr 28, 2026 (Squad-D) — LeadManagerDashboard is no longer rendered here;
// /dashboard now redirects to /lead-manager for the LEAD_MANAGER role. The
// component file is kept (used by no one else) so existing imports elsewhere
// don't break; this can be deleted later once verified no other consumers.
// Executive dashboards (Apr 22, 2026 exec sprint)
import { SalesExecDashboard } from './executive/SalesExecDashboard';
import { ServiceTechDashboard } from './executive/ServiceTechDashboard';
import { ProductionOperatorDashboard } from './executive/ProductionOperatorDashboard';
import { WarehouseExecDashboard } from './executive/WarehouseExecDashboard';
import { AccountsExecDashboard } from './executive/AccountsExecDashboard';
import { FieldPartnerDashboard } from './executive/FieldPartnerDashboard';

/**
 * Heuristic: is the signed-in user a Project / Delivery Manager?
 *
 * `DashboardRoleKey` in roleLayouts.ts does not model PROJECT_MANAGER yet, so
 * `resolveRoleKey()` cannot return it. We detect via designation/role name so
 * Project Managers land on the dedicated ProjectDashboard instead of the
 * legacy widget fallback. Pure UI — zero backend coupling.
 */
function isProjectManager(user: any): boolean {
  const title = (user?.designation ?? '').toLowerCase();
  const roleName = (user?.role?.name ?? '').toLowerCase();
  if (roleName === 'project_manager') return true;
  if (title.includes('project manager') || title.includes('delivery manager')) return true;
  return false;
}

// Preview selects presentation only. Child requests retain the authenticated user.
export function DashboardPage() {
  const { user } = useAuthStore();

  const preview = useProfilePreview(user);
  const presentation = preview ?? user;
  const profile = resolveProfile(presentation);
  if (isPlatformAdmin(user)) return <Navigate to="/admin" replace />;
  if (profile && userLanding(presentation) !== '/dashboard') return <Navigate to={userLanding(presentation)} replace />;

  // Project Manager short-circuit (role key not in DashboardRoleKey enum).
  if (!profile && isProjectManager(presentation)) {
    return <ProjectDashboard />;
  }

  // Map resolved DashboardRoleKey → role dashboard component.
  // Apr 26, 2026 (Squad-B) — Squad-A added LEAD_MANAGER + BACKEND_STAFF to
  // DashboardRoleKey. Belt-and-braces fallback via raw role.name kept for
  // any tenant whose seed has those role rows but resolveRoleKey doesn't
  // map them yet (no harm — switch below also catches them).
  const roleKey: DashboardRoleKey = resolveRoleKey(presentation as any);
  const rawRoleName = ((presentation as any)?.role?.name ?? '').toString().toLowerCase().trim();
  // Apr 28, 2026 (Squad-D) — Lead Manager redirected to dedicated home.
  if (rawRoleName === 'lead_manager') return <Navigate to="/lead-manager" replace />;
  if (rawRoleName === 'backend_staff' || rawRoleName === 'backend_crm' || rawRoleName === 'tele_sales') {
    return <SalesExecDashboard />;
  }

  switch (roleKey) {
    // S2/S4 extend these existing operational screens into the picked dashboards.
    case 'CRM_OPERATIONS': return <CrmOperationsDashboard />;
    case 'HR': return <HrDashboard />;
    case 'UNKNOWN': return <NoAccessPage />;
    case 'OWNER':
      return <AdminDashboard />;
    case 'LEAD_MANAGER':
      // Apr 28, 2026 (Squad-D) — Lead Manager has its own dedicated home.
      return <Navigate to="/lead-manager" replace />;
    case 'BACKEND_STAFF':
      return <SalesExecDashboard />;

    case 'SALES_HEAD':
    case 'SALES_MANAGER':
      return <SalesDashboard />;

    // Sales Executive + Telecaller → SalesExecDashboard.
    // (Apr 24, 2026 — SALES_EXEC_FIELD + SALES_EXEC_OFFICE merged into SALES_EXECUTIVE.)
    case 'SALES_EXECUTIVE':
    case 'TELECALLER':
      return <SalesExecDashboard />;

    case 'FINANCE_MANAGER':
      return <AccountantDashboard />;

    case 'PRODUCTION_HEAD':
      return <ProductionDashboard />;

    // Production Supervisor / Operator → ProductionOperatorDashboard
    case 'PRODUCTION_SUPERVISOR':
      return <ProductionOperatorDashboard />;

    case 'INVENTORY_HEAD':
      return <InventoryDashboard />;

    case 'PURCHASE_HEAD':
      return <ProcurementDashboard />;

    // Service Technician → ServiceTechDashboard
    case 'SERVICE_TECHNICIAN':
      return <ServiceTechDashboard />;

    // Warehouse Executive → WarehouseExecDashboard (auto-routes from role/designation)
    case 'WAREHOUSE_EXECUTIVE':
      return <WarehouseExecDashboard />;

    // Accounts Executive → AccountsExecDashboard (auto-routes from role/designation)
    case 'ACCOUNTS_EXECUTIVE':
      return <AccountsExecDashboard />;

    // Field Partner (installer / channel partner) → FieldPartnerDashboard
    case 'FIELD_PARTNER':
      return <FieldPartnerDashboard />;

    // SERVICE_MANAGER — no dedicated role dashboard component shipped in this
    // sprint. Fall through to the legacy widget view so it still renders.
    default:
      return <LegacyWidgetDashboard />;
  }
}
